/**
 * Meta admin — agency-level App credentials only.
 *
 * The previous master-OAuth flow (single agency login that discovered every
 * client's Ads/Pages/IG/WABA and required per-row "Assign to client"
 * dropdowns) has been retired. Every Meta connection now lives on the
 * per-client page (`/clients/:clientId/meta-ads`), so all this page keeps
 * is the agency-wide app credentials form (App ID / App Secret /
 * Login-for-Business Configuration ID) that powers those per-client
 * OAuth rounds.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import {
  Facebook, Save, ExternalLink, Eye, EyeOff, ShieldCheck, Users,
} from "lucide-react";

export default function MetaIntegrationsPage() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  // Form state
  const [appId, setAppId] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [configId, setConfigId] = useState("");

  // OAuth redirect URI for the per-client flow lives at the same path as
  // before so existing Meta App settings keep working without re-registration.
  const redirectUri = `${window.location.origin}/integrations/meta/callback`;

  const refresh = async () => {
    try {
      const { data } = await api.get("/integrations/meta/settings");
      setSettings(data);
      setAppId(data.app_id || "");
      setConfigId(data.login_for_business_config_id || "");
    } catch (e) {
      // No admin row yet / transient blip — render the empty form silently.
      logger.warn("MetaIntegrations: load settings failed", e);
      setSettings({});
    }
  };

  useEffect(() => { refresh(); }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const payload = {
        app_id: appId.trim(),
        redirect_uri: redirectUri,
        login_for_business_config_id: configId.trim(),
      };
      if (appSecret.trim()) payload.app_secret = appSecret.trim();
      await api.put("/integrations/meta/settings", payload);
      toast.success("Saved");
      setAppSecret("");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!settings) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="space-y-8 max-w-4xl" data-testid="meta-admin-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-gradient-to-br from-[#1877F2] to-[#E1306C] text-white">
          <Facebook className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <div className="label-overline">Integrations</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight">Meta admin</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Agency-level Meta App credentials. Save the App ID, App Secret
            and (optional) Login-for-Business Configuration ID once — every
            per-client Meta OAuth round reuses these to authorise that
            client&apos;s Ad Account / Page / Instagram / WhatsApp assets.
          </p>
        </div>
        <Link to="/integrations/meta/app-review">
          <Button variant="outline" size="sm" className="rounded-sm"
            data-testid="meta-app-review-link">
            <ShieldCheck className="h-4 w-4 mr-1.5" />
            App Review checklist
          </Button>
        </Link>
      </div>

      {/* Per-client info banner — direct admins to where the actual connection lives now. */}
      <div className="rounded-sm border border-blue-200 bg-blue-50 p-4 flex items-start gap-3"
        data-testid="meta-per-client-banner">
        <Users className="h-5 w-5 text-blue-700 mt-0.5 shrink-0" />
        <div className="text-sm text-blue-900">
          <div className="font-semibold">Meta connections now live per-client</div>
          <div className="text-[12px] mt-1 leading-relaxed">
            Connect a Meta account from each client&apos;s Integrations hub
            (Clients → pick a client → Meta). One OAuth grant per client
            scopes the Ad Account, Pages, Instagram and WhatsApp assets
            exclusively to that client — no master discovery, no manual
            assignment dropdowns. This page only holds the agency-wide
            App credentials.
          </div>
        </div>
      </div>

      {/* App credentials card */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-display font-bold text-xl">App credentials</div>
              <p className="text-xs text-zinc-500">
                Find these in the Meta App dashboard at{" "}
                <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer"
                  className="underline">developers.facebook.com/apps <ExternalLink className="inline h-3 w-3" /></a>.
              </p>
            </div>
            <Badge className={`rounded-sm ${settings.configured
              ? "bg-emerald-100 text-emerald-800 border-emerald-200"
              : "bg-amber-100 text-amber-800 border-amber-200"}`}
              data-testid="meta-master-config-badge">
              {settings.configured ? "Configured" : "Secret required"}
            </Badge>
          </div>
          <form onSubmit={save} className="grid md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-overline">Meta App ID</Label>
              <Input value={appId} onChange={(e) => setAppId(e.target.value)}
                className="rounded-sm font-mono text-xs h-10"
                placeholder="1234567890123456"
                data-testid="meta-app-id-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">
                Meta App Secret {settings.has_app_secret && (
                  <span className="ml-2 normal-case tracking-normal text-emerald-700 text-[10px]">· stored</span>
                )}
              </Label>
              <div className="flex gap-2">
                <Input type={showSecret ? "text" : "password"}
                  value={appSecret}
                  onChange={(e) => setAppSecret(e.target.value)}
                  placeholder={settings.has_app_secret ? "•••••••• (leave blank to keep)" : "Paste secret"}
                  className="rounded-sm font-mono text-xs h-10 flex-1"
                  data-testid="meta-app-secret-input" />
                <Button type="button" variant="outline" className="rounded-sm" size="sm"
                  onClick={() => setShowSecret((s) => !s)}>
                  {showSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label className="label-overline">
                Facebook Login for Business — Configuration ID (optional)
                {settings.login_mode === "business" && (
                  <span className="ml-2 normal-case tracking-normal text-emerald-700 text-[10px]">· active</span>
                )}
              </Label>
              <Input value={configId} onChange={(e) => setConfigId(e.target.value)}
                placeholder="1234567890123456 (leave blank for classic OAuth)"
                className="rounded-sm font-mono text-[11px] h-10"
                data-testid="meta-config-id-input" />
              <p className="text-[11px] text-zinc-500">
                Create in Meta App Dashboard at{" "}
                <span className="font-mono">Use Cases → Facebook Login for Business → Configurations</span>.
                When set, scopes come from this configuration (cleaner consent UI, System User tokens). Leave blank to use scope-by-scope OAuth.
              </p>
            </div>

            <div className="md:col-span-2 space-y-1.5">
              <Label className="label-overline">Redirect URI (paste into Meta App)</Label>
              <Input value={redirectUri} readOnly
                className="rounded-sm font-mono text-[11px] h-9 bg-zinc-50"
                onFocus={(e) => e.target.select()}
                data-testid="meta-redirect-uri" />
              <p className="text-[11px] text-zinc-500">
                Add this URI under <span className="font-mono">Use Cases → Facebook Login → Settings → Valid OAuth Redirect URIs</span>.
              </p>
            </div>

            <div className="md:col-span-2 flex justify-end">
              <Button type="submit" disabled={busy}
                className="rounded-sm bg-[#1877F2] hover:bg-[#0c5dc7] text-white"
                data-testid="meta-save-settings">
                <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save credentials"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
